import { faPause, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import Loading from 'components/loading/Loading';
import DialogModal from 'components/modals/DialogModal';
import TicketPauseRegistrationModal from 'components/modals/TicketPauseRegistrationModal';
import useApi from 'helpers/api';
import { formatTime } from 'helpers/utils';
import useCommunication from 'hooks/useCommunication';
import { useEffect, useRef, useState } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import TicketsService from 'services/Tickets.service';

const TicketsPreviewHeader = ({
  data,
  refreshData,
  error,
  success,
  refreshLogs,
  refreshFiles,
  refreshNotes,
  propertiesFormRef
}) => {
  const navigate = useNavigate();
  const api = useApi();
  const [timeSpent, setTimeSpent] = useState('');
  const [showModalRegister, setShowModalRegister] = useState(false);
  const [showDialogConfirm, setShowDialogConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const inputFile = useRef(null);
  const [loading, setLoading] = useState(false);
  const headerRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  const ticketId = data.id;

  // Usando o hook useCommunication para controlar os componentes em TicketInfo
  const { activeComponent, showAddNote, showReplyNote, showForwardNote } =
    useCommunication();

  useEffect(() => {
    attachFile();
  }, [selectedFile]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        setIsSticky(window.scrollY > headerRef.current.offsetTop);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const startTicket = async () => {
    let request = await TicketsService.start(ticketId);
    if (request.ok) {
      refreshData();
      refreshLogs();
      success('Ticket iniciado com sucesso');
    } else {
      error(request);
    }
  };

  const pauseTicket = async body => {
    setLoading(true);
    const response = await TicketsService.pause(ticketId, body);
    if (response.status === 403) {
      error('Você não tem permissão para realizar esta operação');
    } else if (response.userMessage) {
      error(response.userMessage);
    } else {
      refreshData();
      refreshLogs();
      success('Ticket pausado com sucesso');
      handleCloseModalRegister();
    }
    setLoading(false);
  };

  const completeTicket = async () => {
    // Validar campos obrigatórios antes de concluir
    if (propertiesFormRef?.current?.validate) {
      const isValid = propertiesFormRef.current.validate();
      if (!isValid) {
        error('Por favor, preencha todos os campos obrigatórios antes de concluir o ticket.');
        return;
      }
    }

    const response = await TicketsService.complete(ticketId);
    if (!response.userMessage) {
      refreshData();
      refreshLogs();
      success('Ticket concluído com sucesso');
    } else {
      error(response.userMessage);
    }
  };

  const reopenTicket = async () => {
    const response = await TicketsService.reopen(ticketId);
    if (!response.userMessage) {
      refreshData();
      refreshLogs();
      success('Ticket reaberto com sucesso');
    } else {
      error(response.userMessage);
    }
  };

  const deleteTicket = async () => {
    const response = await TicketsService.delete(ticketId);
    if (!response.userMessage) {
      navigate('/tickets');
    } else {
      error(response.userMessage);
    }
  };

  const attachFile = async () => {
    setLoading(true);
    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const request = await api.ticketFiles.attach(formData, ticketId);
      if (request.ok) {
        refreshLogs();
        refreshFiles();
        success('Arquivo anexado com sucesso');
        setSelectedFile(null);
      } else {
        error(request);
      }
    }
    setLoading(false);
  };

  const printTicket = async () => {
    setLoading(true);
    try {
      const response = await TicketsService.print(ticketId);

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error downloading the PDF', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseTicket = async () => {
    let response = await TicketsService.getTimeSpent(ticketId);
    setTimeSpent(response && formatTime(response));
    setShowModalRegister(true);
  };

  const handleFileSelect = e => {
    setSelectedFile(e.target.files[0]);
  };

  const handleCloseModalRegister = () => {
    setShowModalRegister(false);
  };

  return (
    <>
      {loading && <Loading />}
      <TicketPauseRegistrationModal
        ticketId={ticketId}
        showModal={showModalRegister}
        handleClose={() => setShowModalRegister(false)}
        handleSubmit={pauseTicket}
        data={timeSpent}
        isLoading={loading}
      />

      <DialogModal
        message="Tem certeza que deseja excluir este ticket?"
        showModal={showDialogConfirm}
        handleClose={() => setShowDialogConfirm(false)}
        response={deleteTicket}
      />

      <Card ref={headerRef} className={isSticky ? 'sticky-header' : ''}>
        <Card.Header className="d-flex flex-between-center">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="arrow-left"
            as={Link}
            to={'/tickets'}
          />
          <Flex>
            {data.status && data.status.status === 'IN_PROGRESS' && (
              <IconButton
                variant="falcon-default"
                size="sm"
                icon={faPause}
                transform="shrink-2"
                iconAlign="middle"
                className="me-2"
                onClick={() => handlePauseTicket()}
              >
                <span className="d-none d-md-inline-block ms-1">Pausar</span>
              </IconButton>
            )}

            {data.status &&
              data.status.status !== 'IN_PROGRESS' &&
              data.status.status !== 'COMPLETED' &&
              data.status.status !== 'CLOSED' && (
                <IconButton
                  variant="falcon-default"
                  size="sm"
                  icon="play"
                  transform="shrink-2"
                  iconAlign="middle"
                  className="me-2"
                  onClick={() => startTicket()}
                >
                  <span className="d-none d-md-inline-block ms-1">Iniciar</span>
                </IconButton>
              )}

            {data.status && data.status.status === 'COMPLETED' && (
              <IconButton
                variant="falcon-default"
                size="sm"
                icon={faUndo}
                transform="shrink-2"
                iconAlign="middle"
                className="me-2"
                onClick={() => reopenTicket()}
              >
                <span className="d-none d-md-inline-block ms-1">
                  Reabrir ticket
                </span>
              </IconButton>
            )}

            {data.status &&
              data.status.status !== 'IN_PROGRESS' &&
              data.status.status !== 'COMPLETED' &&
              data.status.status !== 'CLOSED' && (
                <IconButton
                  variant="falcon-default"
                  size="sm"
                  icon="check"
                  transform="shrink-2"
                  iconAlign="middle"
                  className="me-2"
                  onClick={() => completeTicket()}
                >
                  <span className="d-none d-md-inline-block ms-1">
                    Concluir
                  </span>
                </IconButton>
              )}

            <IconButton
              variant="falcon-default"
              size="sm"
              icon="print"
              transform="shrink-2"
              iconAlign="middle"
              className="me-2"
              onClick={() => printTicket()}
            >
              <span className="d-none d-md-inline-block ms-1">Imprimir</span>
            </IconButton>

            <IconButton
              variant="falcon-default"
              size="sm"
              icon="trash-alt"
              transform="shrink-2"
              iconAlign="middle"
              className="d-none d-sm-block"
              onClick={() => setShowDialogConfirm(true)}
            >
              <span className="d-none d-md-inline-block ms-1">Excluir</span>
            </IconButton>

            {/* Botões de comunicação que controlam os componentes em TicketInfo */}
            {data.status &&
              data.status.status !== 'COMPLETED' &&
              data.status.status !== 'CLOSED' && (
                <>
                  <Dropdown
                    align="end"
                    className="btn-reveal-trigger d-inline-block ms-2"
                  >
                    <Dropdown.Toggle split variant="falcon-default" size="sm">
                      <FontAwesomeIcon icon="ellipsis-v" className="fs--2" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="border py-0">
                      <div className="py-2">
                        <Dropdown.Item onClick={showAddNote}>
                          Adicionar nota
                        </Dropdown.Item>
                        <Dropdown.Item onClick={showReplyNote}>
                          Responder
                        </Dropdown.Item>
                        <Dropdown.Item onClick={showForwardNote}>
                          Encaminhar
                        </Dropdown.Item>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              )}
          </Flex>
        </Card.Header>
      </Card>
    </>
  );
};

export default TicketsPreviewHeader;
